import { Suspense, lazy, useCallback, useState } from 'react'
import { HeroContent } from './hero-content'
import { WebglFallback } from './webgl-fallback'
import { HeroErrorBoundary } from './hero-error-boundary'
import { prefersReducedMotion, isCoarsePointer } from '@/lib/motion'
import type { HeroFuturisticProps } from './types'

// Client-only, code-split: the 3D scene must never block first paint or LCP
// (§4.7/§11) — HeroContent renders immediately and independently.
const HeroCanvas = lazy(() => import('./hero-canvas').then((m) => ({ default: m.HeroCanvas })))

/**
 * Cheap capability probe, run BEFORE the ~1.3 MB canvas chunk + textures are
 * requested: can this browser create a WebGL2 context at all? If not, no
 * backend of `three/webgpu` can render, so skip the download entirely and go
 * straight to the CSS fallback. The probe context is released immediately.
 */
function supportsWebGL2(): boolean {
  try {
    const probe = document.createElement('canvas')
    const gl = probe.getContext('webgl2')
    if (!gl) return false
    gl.getExtension('WEBGL_lose_context')?.loseContext()
    return true
  } catch {
    return false
  }
}

/**
 * Which renderer backend to ask three.js for.
 *  - 'auto'  — WebGPU when available, WebGL2 otherwise (three decides).
 *  - 'webgl' — force WebGL2, never touch WebGPU.
 * Touch devices start on 'webgl': WebGPU on Android browsers is the flaky part
 * (Opera Mobile exposes `navigator.gpu` but can hang / fail creating the
 * device), while WebGL2 runs the exact same TSL shader reliably. Desktop starts
 * on 'auto'. Any failure on 'auto' retries ONCE on 'webgl'; a failure on
 * 'webgl' gives up and shows the static CSS fallback.
 */
type Backend = 'auto' | 'webgl'

/**
 * Futuristic EA Studio hero (master prompt §4), `three/webgpu` + TSL pipeline
 * (see hero-scene.tsx / post-processing.tsx / hero-canvas.tsx).
 * Composition:
 *   - <HeroContent> — real semantic copy, always rendered, drives LCP.
 *   - <HeroCanvas> — decorative scene, lazy-loaded. Mounted on every device
 *     that can create a WebGL2 context, INCLUDING reduced-motion (which gets a
 *     single static frame instead of the animated scene, rather than losing the
 *     blob entirely) and touch devices (lower dpr, no MSAA, no pointer parallax,
 *     ~30fps render cap).
 *   - <WebglFallback> — static CSS glow, used only when the device can't render
 *     the scene at all (no WebGL2, or both backends failed), so the hero is
 *     never blank (§4.7).
 */
export function HeroFuturistic({ id = 'hero' }: HeroFuturisticProps) {
  // Lazy initializer instead of useEffect: client-only SPA (no SSR/hydration to
  // keep in sync), so reading these synchronously on first render avoids a
  // one-frame flash of the wrong hero.
  const [capabilities] = useState(() => ({
    reducedMotion: prefersReducedMotion(),
    coarsePointer: isCoarsePointer(),
    webgl2: supportsWebGL2(),
  }))
  const [backend, setBackend] = useState<Backend>(() => (capabilities.coarsePointer ? 'webgl' : 'auto'))
  const [renderFailed, setRenderFailed] = useState(false)

  const handleFailure = useCallback(() => {
    if (backend === 'auto') {
      console.warn('[hero] renderer failed on the automatic backend — retrying once with forced WebGL2')
      setBackend('webgl')
    } else {
      console.warn('[hero] renderer failed on WebGL2 — showing the static CSS fallback')
      setRenderFailed(true)
    }
  }, [backend])

  const showCanvas = capabilities.webgl2 && !renderFailed

  return (
    <section id={id} className="relative w-full overflow-hidden bg-background">
      <div className="absolute inset-0" aria-hidden="true">
        {showCanvas ? (
          // `key={backend}` remounts boundary + canvas (a fresh <canvas> element,
          // which a failed WebGPU attempt can't hand back cleanly) for the retry.
          <HeroErrorBoundary key={backend} fallback={<WebglFallback />} onError={handleFailure}>
            {/* No fallback here: briefly showing the old glow+grid while the
                chunk downloads reads as two different hero designs loading in
                sequence. The section is already `bg-background` (the same dark
                navy the scene sits on), so rendering nothing for that gap is a
                plain dark backdrop, not a broken one. */}
            <Suspense fallback={null}>
              <HeroCanvas
                reducedMotion={capabilities.reducedMotion}
                coarsePointer={capabilities.coarsePointer}
                forceWebGL={backend === 'webgl'}
                onInitFailure={handleFailure}
              />
            </Suspense>
          </HeroErrorBoundary>
        ) : (
          <WebglFallback />
        )}
      </div>

      <HeroContent reducedMotion={capabilities.reducedMotion} />
    </section>
  )
}
